<script setup>
/**
 * The case side of the inbox bridge, in the conversation Details sidebar: which
 * tickets this conversation is attached to, plus the two ways to attach it to
 * another one.
 *
 * Both writes go to the conversation's own `support_case_links` endpoint, which
 * runs the same commands the workroom's buttons run — escalating never touches
 * this conversation's status, and the role is the server's to decide: a case
 * opened from here becomes this thread's `primary_customer` link (that is where
 * customer progress is published), an existing case attached from here becomes
 * `related`, because it may already have a customer thread.
 *
 * The panel owns its own state rather than the case store's: the store's list
 * state belongs to the queue screen, and a sidebar section must not move the
 * page an agent left behind in another tab.
 */
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useCaseCreateDialog } from 'dashboard/composables/useCaseCreateDialog';
import ConversationApi from 'dashboard/api/inbox/conversation';
import SupportCasesAPI from 'dashboard/api/supportCases';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CaseCreateDialog from './CaseCreateDialog.vue';
import StagePill from './StagePill.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  camelizeSupportCase,
  camelizeSupportCaseLink,
} from 'dashboard/stores/supportCases';

const props = defineProps({
  // The conversation's display id — what the dashboard's payloads call `id`,
  // and what every conversation-nested endpoint routes on.
  conversationId: { type: [Number, String], required: true },
  // Prefill for the create dialog — the conversation's subject, handed down
  // by the panel because this section only knows the conversation by id.
  initialTitle: { type: String, default: '' },
  // `{ id, name }` — the conversation's contact, shown as the requester on a
  // type that keeps the customer posted.
  requesterContact: { type: Object, default: null },
});

const { t } = useI18n();
const router = useRouter();

const links = ref([]);
const isLinking = ref(false);
const linkNumber = ref('');

// The same create flow the conversation header's quick action runs, so both
// entry points open one dialog and one escalation.
const {
  dialogRef: createDialogRef,
  caseTypes,
  isSubmitting,
  openDialog: openCreateDialog,
  onSubmit: onCreate,
} = useCaseCreateDialog(() => props.conversationId);

const fetchLinks = async () => {
  try {
    const { data } = await ConversationApi.getSupportCaseLinks(
      props.conversationId
    );
    links.value = camelizeSupportCaseLink(data.payload);
  } catch (error) {
    // A conversation on an account without the engine answers 404 for this
    // endpoint by design, and a section that shouts about it would shout on
    // every such conversation. The list stays empty.
    links.value = [];
  }
};

const openCase = link =>
  router.push({
    name: 'support_cases_show',
    params: { displayId: String(link.supportCase.displayId) },
  });

// A refusal from the bridge states itself: the commands answer 422 with an
// i18n'd sentence of their own ("This case is archived. Restore it before
// making changes."), which is more use to an agent than any line written here
// and stays translated. It is also the only copy of the rule — a client-side
// pre-check would be a second one, and a racy one, since a case can be archived
// between this panel's read and the write. The stated fallback is for the
// failures that carry no sentence at all: a dropped request, a 500.
const refusal = (error, stated) => error?.response?.data?.message || stated;

// Agents quote the display id (#142); the link endpoint takes the case's own
// id, which is what the read handed out. So the number is resolved through the
// case read first — which is also how a number that names no case the agent may
// see becomes an honest "not here" instead of a failed write.
const onLink = async () => {
  const displayId = linkNumber.value.trim().replace(/^#/, '');
  if (!displayId || isLinking.value) return;

  isLinking.value = true;
  try {
    const { data } = await SupportCasesAPI.show(displayId);
    const supportCase = camelizeSupportCase(data);
    await ConversationApi.createSupportCaseLink(props.conversationId, {
      supportCaseId: supportCase.id,
    });
    linkNumber.value = '';
    await fetchLinks();
    useAlert(t('SUPPORT_CASES.CONVERSATION.LINK_SUCCESS'));
  } catch (error) {
    // The 404 is this flow's own sentence: it means the number named no ticket
    // this agent may see, which the server answers with nothing to say.
    useAlert(
      error?.response?.status === 404
        ? t('SUPPORT_CASES.CONVERSATION.LINK_NOT_FOUND')
        : refusal(error, t('SUPPORT_CASES.CONVERSATION.LINK_ERROR'))
    );
  } finally {
    isLinking.value = false;
  }
};

watch(() => props.conversationId, fetchLinks);
onMounted(fetchLinks);
// A create from any surface — this section's own dialog or the header's quick
// action — lands here, so the list always shows the ticket it just grew.
useEmitter(BUS_EVENTS.SUPPORT_CASE_LINKS_UPDATED, fetchLinks);
</script>

<template>
  <div
    class="flex flex-col gap-1.5 px-4 py-3 border-b border-n-weak"
    data-testid="conversation-cases-section"
  >
    <h4 class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
      {{ t('SUPPORT_CASES.CONVERSATION.TITLE') }}
    </h4>

    <p
      v-if="!links.length"
      class="mb-0 text-xs text-n-slate-11"
      data-testid="conversation-cases-empty"
    >
      {{ t('SUPPORT_CASES.CONVERSATION.EMPTY') }}
    </p>

    <button
      v-for="link in links"
      :key="link.id"
      type="button"
      class="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-n-alpha-1 text-start hover:bg-n-alpha-2"
      :aria-label="
        t('SUPPORT_CASES.CONVERSATION.OPEN', {
          id: link.supportCase.displayId,
        })
      "
      :data-testid="`conversation-case-${link.supportCase.displayId}`"
      @click="openCase(link)"
    >
      <span class="text-xs shrink-0 tabular-nums text-n-slate-10">
        {{
          t('SUPPORT_CASES.LIST.DISPLAY_ID', { id: link.supportCase.displayId })
        }}
      </span>
      <span class="flex-1 min-w-0 text-xs font-medium truncate text-n-slate-12">
        {{ link.supportCase.title }}
      </span>
      <StagePill
        v-if="link.supportCase.caseStage"
        :stage="link.supportCase.caseStage"
      />
    </button>

    <div class="flex items-center gap-2 mt-1">
      <Input
        v-model="linkNumber"
        size="sm"
        class="flex-1 min-w-0"
        :aria-label="t('SUPPORT_CASES.CONVERSATION.LINK_LABEL')"
        :placeholder="t('SUPPORT_CASES.CONVERSATION.LINK_PLACEHOLDER')"
        data-testid="conversation-case-link-input"
        @enter="onLink"
      />
      <Button
        size="sm"
        variant="faded"
        color="slate"
        :label="t('SUPPORT_CASES.CONVERSATION.LINK_SUBMIT')"
        :disabled="isLinking || !linkNumber.trim()"
        data-testid="conversation-case-link-submit"
        @click="onLink"
      />
    </div>

    <Button
      size="sm"
      variant="link"
      color="slate"
      icon="i-lucide-plus"
      class="self-start"
      :label="t('SUPPORT_CASES.CONVERSATION.CREATE')"
      data-testid="conversation-case-create"
      @click="openCreateDialog"
    />

    <CaseCreateDialog
      ref="createDialogRef"
      :case-types="caseTypes"
      :is-submitting="isSubmitting"
      :initial-title="initialTitle"
      :requester-contact="requesterContact"
      @submit="onCreate"
    />
  </div>
</template>
